/**
 * Sentry initialization for the Vite SPA.
 * Uses React Router v7 browser-tracing integration; no server-side instrumentation.
 */

import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { PLUGINS } from "@/config/constants";
import { ENV } from "@/env";
import { isChunkLoadError } from "@/lib/utils/chunk-load";
import { scrubBreadcrumb, scrubEvent, scrubLog, scrubSpan } from "@/lib/sentry-scrub";
import type { AppTags } from "@speakai/common/src/observability/types";

const APP_TAGS = { app: "speak-web", platform: "web", runtime: "browser", channel: "web" } satisfies AppTags;
const BUSY_ROUTE_RATE = 0.2;
const QUIET_ROUTE_RATE = 0.02;
const DEFAULT_ROUTE_RATE = 0.1;
const BUSY_ROUTES = /^\/(home|upload|folder|recorder|insight)(\/|$)/;
const QUIET_ROUTES = /^\/(auth|pricing)(\/|$)/;

const IGNORED_RESOURCE_SPANS = [
  "resource.script",
  "resource.link",
  "resource.img",
  "resource.css",
  "resource.other",
];

const EXTENSION_URLS = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
  /^safari-web-extension:\/\//i,
];

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Anchored matchers for the Speak API origin only; a bare string would match every URL when empty. */
function apiOriginMatchers(): RegExp[] {
  const origins = new Set(["https://api.speakai.co"]);
  try {
    origins.add(new URL(ENV.API_URL).origin);
  } catch {
    // An unparsable configured API URL leaves only the production origin.
  }
  return [...origins].map((origin) => new RegExp(`^${escapeRegExp(origin)}(?:/|$)`));
}

/** Root-span sample rate by pathname; a sampled parent decision always wins. */
export function tracesSampler(context: {
  name: string;
  inheritOrSampleWith: (fallbackSampleRate: number) => number;
}): number {
  const pathname = context.name.startsWith("/")
    ? context.name
    : typeof window !== "undefined"
      ? window.location.pathname
      : "/";
  if (QUIET_ROUTES.test(pathname)) return context.inheritOrSampleWith(QUIET_ROUTE_RATE);
  if (BUSY_ROUTES.test(pathname)) return context.inheritOrSampleWith(BUSY_ROUTE_RATE);
  return context.inheritOrSampleWith(DEFAULT_ROUTE_RATE);
}

/** A first-party frame's filename always ends in a real source/bundle extension. */
const RESOLVABLE_FRAME_EXTENSION = /\.(?:[cm]?[jt]sx?)(?:\?.*)?$/i;

/**
 * True when a stack frame's filename is a real, resolvable first-party location.
 * Some iOS WebKit in-app browsers (Gmail, Google Search, and similar embedded
 * webviews) inject scripts whose own source is unresolvable; when that happens
 * the browser reports the frame's filename as the current document's pathname
 * (e.g. "/survey", "/auth/register") rather than "<anonymous>", so a check for
 * "<anonymous>" alone lets that noise through as if it were first-party code.
 * A bare route path has no file extension; real app code and bundle chunks do.
 */
function isFirstPartyFrame(filename: string | undefined): boolean {
  if (!filename || filename === "<anonymous>" || filename.startsWith("<")) return false;
  return RESOLVABLE_FRAME_EXTENSION.test(filename);
}

/**
 * True for the SDK's global-handler mechanism, matched by suffix rather than exact
 * equality. The installed `@sentry/react` (v10) reports it as
 * "auto.browser.global_handlers.onerror" / "...onunhandledrejection", not the bare
 * "onerror" / "onunhandledrejection" this check used to compare against — so the
 * strict `===` never matched and every one of these events reached Sentry live
 * (see SPEAK-CLIENT-NK). A suffix match covers both the bare and prefixed forms.
 */
function isGlobalHandlerMechanism(mechanism: string | undefined): boolean {
  if (!mechanism) return false;
  return (
    mechanism === "onerror" ||
    mechanism === "onunhandledrejection" ||
    mechanism.endsWith(".onerror") ||
    mechanism.endsWith(".onunhandledrejection")
  );
}

export function initSentry(): void {
  if (!import.meta.env.PROD || !PLUGINS.sentry.isActive) return;

  Sentry.init({
    dsn: PLUGINS.sentry.dsn,
    environment: ENV.APP_ENV,
    release: __APP_RELEASE__,
    initialScope: { tags: APP_TAGS, attributes: APP_TAGS },
    denyUrls: EXTENSION_URLS,
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSendSpan: scrubSpan,
    beforeSendLog: scrubLog,
    ignoreErrors: [
      /Cannot close a closed AudioContext/i,
      // Third-party Source Pixel script (not our code) throws several distinct
      // messages ("is deprecated", "no domain returned from API", ...); match
      // the whole family instead of one literal string. See SPEAK-CLIENT-N6.
      /Source Pixel/i,
      /px\.ads\.linkedin\.com/i,
      /be\.source\.app/i,
    ],
    beforeSend(event, hint) {
      // Chunk-load errors are auto-recovered via the route/dashboard error
      // boundary (window.location.reload). Sending them to Sentry creates
      // noise on every redeploy without surfacing actionable signal.
      if (
        isChunkLoadError(hint?.originalException) ||
        isChunkLoadError(event.message) ||
        event.exception?.values?.some((value) => isChunkLoadError(value.value))
      ) {
        return null;
      }
      if (event.message === "chunk_load_auto_reload") return null;

      // Drop console.error events whose only frame has no resolvable source
      // (filename === "<anonymous>" / undefined). These come from in-app
      // browser script injection (Google/Gmail iOS webviews) and contain no
      // first-party code path to act on.
      if (event.logger === "console") {
        const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
        const hasFirstPartyFrame = frames.some((f) => isFirstPartyFrame(f.filename));
        if (frames.length > 0 && !hasFirstPartyFrame) return null;
      }

      // Same injected-script noise, but arriving via the global onerror /
      // unhandledrejection handlers instead of console (e.g. "Maximum call stack
      // size exceeded" thrown by scripts iOS WebKit in-app browsers inject),
      // with frames that have no first-party code path, sometimes reported as
      // the current page's own pathname rather than "<anonymous>". See SPEAK-CLIENT-G.
      {
        const exc = event.exception?.values?.[0];
        const mechanism = exc?.mechanism?.type;
        const frames = exc?.stacktrace?.frames ?? [];
        const hasFirstPartyFrame = frames.some((f) => isFirstPartyFrame(f.filename));
        if (isGlobalHandlerMechanism(mechanism) && !hasFirstPartyFrame) {
          return null;
        }
      }

      return scrubEvent(event);
    },
    tracesSampler,
    tracePropagationTargets: apiOriginMatchers(),
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
        // Native-UI triggers (file pickers, popups) mutate no DOM; this attribute opts them out of dead-click detection.
        slowClickIgnoreSelectors: ["[data-slow-click-ignore]"],
        networkDetailAllowUrls: [
          import.meta.env.NEXT_PUBLIC_API_URL || "",
          "https://api.speakai.co",
        ],
        networkCaptureBodies: false,
        networkRequestHeaders: ["Content-Type"],
        networkResponseHeaders: ["Content-Type"],
        // Exclude /upload/record from Replay — live transcript text is user speech (PII risk).
        beforeAddRecordingEvent: (event) => {
          if (
            typeof window !== "undefined" &&
            window.location.pathname.startsWith("/upload/record")
          ) {
            return null;
          }
          return event;
        },
      }),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        ignoreResourceSpans: IGNORED_RESOURCE_SPANS,
      }),
      Sentry.captureConsoleIntegration({ levels: ["error"] }),
    ],
  });
}
